import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Phone,
  Email,
  LocationOn,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { fadeInUp, viewportConfig } from '../../utils/animations';
import { COMPANY_INFO, CONTACT_INFO, NAV_ITEMS, PEGASUS_URLS } from '../../utils/constants';
import useScrollTo from '../../hooks/useScrollTo';
import logo from '../../assets/logo-no-background.png';

const Footer = () => {
  const { scrollToSection, scrollToTop } = useScrollTo();
  const currentYear = new Date().getFullYear();

  const quickLinks = NAV_ITEMS.slice(0, 4);
  const serviceLinks = [
    { label: 'M-Pesa Reconciliation', id: 'why-us' },
    { label: 'Tenant Billing', id: 'why-us' },
    { label: 'Accounting Suite', id: 'why-us' },
    { label: 'Pricing Plans', id: 'pricing' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'primary.main',
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: { xs: 3, md: 4 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
            >
              <Box
                component="img"
                src={logo}
                alt={COMPANY_INFO.name}
                sx={{
                  height: 50,
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                  mb: 2,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  mb: 3,
                  lineHeight: 1.8,
                  maxWidth: 300,
                }}
              >
                {COMPANY_INFO.description}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  aria-label="Facebook"
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  <Facebook fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Twitter"
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  <Twitter fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  <Instagram fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="LinkedIn"
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'secondary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  <LinkedIn fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: '1rem',
                }}
              >
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {quickLinks.map((link) => (
                  <Link
                    key={link.id}
                    component="button"
                    onClick={() => scrollToSection(link.id)}
                    underline="none"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'secondary.main',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: '1rem',
                }}
              >
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {serviceLinks.map((link) => (
                  <Link
                    key={link.label}
                    component="button"
                    onClick={() => scrollToSection(link.id)}
                    underline="none"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.875rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'secondary.main',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInUp}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: '1rem',
                }}
              >
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Phone sx={{ color: 'secondary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {CONTACT_INFO.phone.join(' / ')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Email sx={{ color: 'secondary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {CONTACT_INFO.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationOn sx={{ color: 'secondary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {CONTACT_INFO.address}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Link
                  href={PEGASUS_URLS.login}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    display: 'inline-block',
                    px: 3,
                    py: 1,
                    backgroundColor: 'secondary.main',
                    color: 'primary.main',
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'secondary.light',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Access Pegasus PMS
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider
          sx={{
            my: 4,
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'center' },
              gap: { xs: 1, sm: 2 },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
            >
              &copy; {currentYear} {COMPANY_INFO.name}. All rights reserved.
            </Typography>
            <Link
              component={RouterLink}
              to="/privacy-policy"
              underline="hover"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.875rem',
                '&:hover': { color: 'secondary.main' },
              }}
            >
              Privacy Policy
            </Link>
          </Box>

          <IconButton
            onClick={scrollToTop}
            aria-label="Scroll to top"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'secondary.main',
                color: 'primary.main',
              },
            }}
          >
            <KeyboardArrowUp />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
